<script setup>
import { computed } from 'vue';
import { format } from 'date-fns';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  order: {
    type: Object,
    required: true,
  },
});

const { t, locale } = useI18n();

const formatDate = dateString => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  return format(date, 'MMM d, yyyy');
};

const formatCurrency = (amount, currency) => {
  const numericAmount = Number(amount) || 0;
  return new Intl.NumberFormat(locale.value, {
    style: 'currency',
    currency: currency || 'BRL',
  }).format(numericAmount);
};

const getPaymentStatusClass = status => {
  const classes = { paid: 'bg-n-teal-5 text-n-teal-12' };
  return classes[status] || 'bg-n-solid-3 text-n-slate-12';
};

const orderStatus = computed(() => {
  const { status } = props.order;
  if (!status) return '';

  switch (status?.toUpperCase()) {
    case 'OPEN':
      return t('CONVERSATION_SIDEBAR.TRAY.ORDER_STATUS.OPEN');
    case 'APPROVED':
      return t('CONVERSATION_SIDEBAR.TRAY.ORDER_STATUS.APPROVED');
    case 'PAID':
      return t('CONVERSATION_SIDEBAR.TRAY.ORDER_STATUS.PAID');
    case 'CANCELLED':
      return t('CONVERSATION_SIDEBAR.TRAY.ORDER_STATUS.CANCELLED');
    case 'DELIVERED':
      return t('CONVERSATION_SIDEBAR.TRAY.ORDER_STATUS.DELIVERED');
    case 'PENDING':
      return t('CONVERSATION_SIDEBAR.TRAY.ORDER_STATUS.PENDING');
    default:
      return status;
  }
});

const paymentStatus = computed(() => {
  const status = props.order.payment_status;
  if (!status) return '';

  switch (status?.toUpperCase()) {
    case 'PAID':
      return t('CONVERSATION_SIDEBAR.TRAY.PAYMENT_STATUS.PAID');
    case 'PENDING':
      return t('CONVERSATION_SIDEBAR.TRAY.PAYMENT_STATUS.PENDING');
    default:
      return status;
  }
});
</script>

<template>
  <div
    class="flex flex-col gap-1.5 border-b border-n-weak py-3 last:border-b-0"
  >
    <div class="flex items-center justify-between gap-2">
      <div class="flex min-w-0 font-medium">
        <a
          v-if="order.admin_url"
          :href="order.admin_url"
          target="_blank"
          rel="noopener noreferrer"
          class="cursor-pointer truncate text-n-slate-12 hover:underline"
        >
          {{
            $t('CONVERSATION_SIDEBAR.TRAY.ORDER_ID', {
              id: order.number || order.id,
            })
          }}
          <i class="i-lucide-external-link pl-1" />
        </a>
        <span v-else class="truncate text-n-slate-12">
          {{
            $t('CONVERSATION_SIDEBAR.TRAY.ORDER_ID', {
              id: order.number || order.id,
            })
          }}
        </span>
      </div>
      <div
        :class="getPaymentStatusClass(order.payment_status)"
        class="truncate rounded px-2 py-1 text-xs capitalize"
        :title="paymentStatus"
      >
        {{ paymentStatus }}
      </div>
    </div>
    <div class="text-sm text-n-slate-12">
      <span
        v-if="formatDate(order.created_at)"
        class="border-r border-n-weak pr-2 text-n-slate-11"
      >
        {{ formatDate(order.created_at) }}
      </span>
      <span class="pl-2 text-n-slate-11">
        {{ formatCurrency(order.total, order.currency) }}
      </span>
    </div>
    <div v-if="orderStatus">
      <span class="font-medium capitalize text-n-slate-11" :title="orderStatus">
        {{ orderStatus }}
      </span>
    </div>
  </div>
</template>
